import { useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { rupiah, statusInfo, FASMART_CATS, branchPrice, suggestPrice, CATEGORY_MARGIN } from "@/lib/format";
import BarcodeScanner from "@/components/BarcodeScanner";
import ImportExcel from "@/components/ImportExcel";
import BulkWatermark from "@/components/BulkWatermark";
import PricingCheckTab from "@/components/PricingCheckTab";
import BackupTab from "@/components/BackupTab";
import PrinterTab from "@/components/PrinterTab";
import TransactionReport from "@/components/TransactionReport";
import RestockList from "@/components/RestockList";
import NotificationBell from "@/components/NotificationBell";
import { useBranch } from "@/context/BranchContext";
import FinanceTab from "@/components/FinanceTab";
import AuditTab from "@/components/AuditTab";
import BranchesTab from "@/components/BranchesTab";
import { requestPin, PinGateModal } from "@/components/PinGate";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, TrendingUp, Users, ShoppingBag, Package, DollarSign, MessageCircle, X, Printer } from "lucide-react";

const CATS = ["Sembako", "Minuman", "Snack & Biskuit", "Mie Instan", "Bumbu & Saus", "Kebersihan Rumah", "Perawatan Diri", "Ibu & Bayi", "Perlengkapan Rumah", "Obat & Kesehatan", "Alat Tulis & Kantor", "EsKrim & FrozenFood", "FASQUA", "FASLOOK", "BOBA-MIE", "FAS TRADING", "FAS KOST", "FAS LAUNDRY", "FAS MUSIC", "FASNET WiFi", "FAS Printing"];
const ORDER_FLOW = ["baru", "cek_transfer", "diterima", "disiapkan", "akan_dikirim", "dikirim", "selesai"];
const NEXT_STATUS_LABEL = {
  cek_transfer: "💳 Cek Transferan",
  diterima: "✅ Terima Pesanan",
  disiapkan: "🧑‍🍳 Menyiapkan Pesanan",
  akan_dikirim: "📦 Akan dikirim",
  dikirim: "🛵 Sedang dikirim",
  selesai: "✔️ Pesanan Selesai",
  dibatalkan: "❌ Batalkan",
};

function useNewOrderAlert() {
  const [newCount, setNewCount] = useState(0);
  const [tick, setTick] = useState(0);
  const prevRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const r = await api.get("/orders");
        if (!alive) return;
        const c = r.data.filter((o) => o.status === "baru").length;
        if (prevRef.current !== null && c > prevRef.current) {
          toast.success("🔔 Pesanan baru masuk!", { description: `${c} pesanan menunggu diterima. Buka tab Pesanan.` });
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 880;
            gain.gain.value = 0.15;
            osc.start();
            osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.15);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
            osc.stop(ctx.currentTime + 0.5);
          } catch {}
        }
        prevRef.current = c;
        setNewCount(c);
      } catch {}
    };
    check();
    const t = setInterval(check, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [tick]);
  return { newCount, refresh: () => setTick((t) => t + 1) };
}

function DailyReportTab() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [date, setDate] = useState(() => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loadingRep, setLoadingRep] = useState(false);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoadingRep(true);
    const params = new URLSearchParams();
    if (branchId) params.set("branch_id", branchId);
    if (date) params.set("date", date);
    api.get(`/admin/daily-report?${params}`).then((r) => setReport(r.data)).catch(() => toast.error("Gagal memuat laporan")).finally(() => setLoadingRep(false));
  }, [date, branchId]);

  const totalOnline = report?.branches.reduce((s, b) => s + b.online_revenue, 0) || 0;
  const totalPos = report?.branches.reduce((s, b) => s + b.pos_revenue, 0) || 0;
  const cashierRows = (report?.branches || []).flatMap((b) => (b.cashiers || []).map((c) => ({ ...c, branch_name: b.branch_name })));
  const branchLabel = branchId ? (branches.find((b) => b.id === branchId)?.name || "") : "Semua Cabang";
  const printDaily = () => {
    document.body.classList.add("printing-daily");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-daily"), 500);
  };

  return (
    <div className="space-y-4" data-testid="daily-report-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <Input data-testid="report-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
        {!user.branch_id && (
          <select data-testid="report-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-2 bg-white">
            <option value="">Semua Cabang</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        {report && (
          <button data-testid="daily-report-print-btn" onClick={printDaily} className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-bold flex items-center gap-1.5 hover:bg-emerald-700 transition"><Printer className="w-4 h-4" /> Cetak Laporan</button>
        )}
      </div>
      {!user.branch_id && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">
          <div className="text-xs font-mono uppercase tracking-widest text-rose-700 mb-2">Zona Berbahaya — khusus super admin</div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="admin-clear-orders-btn" onClick={async () => { const pin = await requestPin(); if (!pin) return; try { const r = await api.delete(`/admin/orders?pin=${pin}`); toast.success(`${r.data.deleted} transaksi online dihapus`); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); } }} className="px-4 py-2 rounded-full bg-rose-600 text-white text-xs font-bold hover:bg-rose-700">🗑 Hapus Semua Transaksi Online</button>
            <button data-testid="admin-clear-pos-btn" onClick={async () => { const pin = await requestPin(); if (!pin) return; try { const r = await api.delete(`/admin/pos-sales?pin=${pin}`); toast.success(`${r.data.deleted} penjualan kasir dihapus`); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); } }} className="px-4 py-2 rounded-full bg-rose-600 text-white text-xs font-bold hover:bg-rose-700">🗑 Hapus Semua Riwayat Kasir</button>
          </div>
        </div>
      )}
      {loadingRep && <p className="text-sm text-stone-400">Memuat laporan...</p>}
      {report && (
        <div id="daily-report-print" data-testid="daily-report-print">
          <div className="print-only" style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 900, fontSize: 18 }}>FASMART — Laporan Penjualan Harian</div>
            <div style={{ fontSize: 12 }}>Tanggal: {report.date} · Cabang: {branchLabel}</div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-stone-500">Pesanan Online</div>
              <div data-testid="report-online-total" className="font-display font-black text-2xl text-stone-900 mt-1">{rupiah(totalOnline)}</div>
              <div className="text-xs text-stone-500">{report.branches.reduce((s, b) => s + b.online_count, 0)} pesanan</div>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-stone-500">Kasir Toko</div>
              <div data-testid="report-pos-total" className="font-display font-black text-2xl text-stone-900 mt-1">{rupiah(totalPos)}</div>
              <div className="text-xs text-stone-500">{report.branches.reduce((s, b) => s + b.pos_count, 0)} transaksi</div>
            </div>
            <div className="bg-emerald-700 rounded-2xl p-4 text-white">
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-200">Omzet Gabungan</div>
              <div data-testid="report-grand-total" className="font-display font-black text-2xl mt-1">{rupiah(report.grand_total)}</div>
              <div className="text-xs text-emerald-200">{report.date}</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5DECF] overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]"><th className="p-3">Cabang</th><th className="p-3">Online</th><th className="p-3">Kasir</th><th className="p-3">Total</th></tr></thead>
              <tbody>
                {report.branches.map((b) => (
                  <tr key={b.branch_id} className="border-b border-stone-100 last:border-0" data-testid={`report-row-${b.branch_id}`}>
                    <td className="p-3 font-semibold">{b.branch_name}</td>
                    <td className="p-3">{b.online_count} · {rupiah(b.online_revenue)}</td>
                    <td className="p-3">{b.pos_count} · {rupiah(b.pos_revenue)}</td>
                    <td className="p-3 font-bold text-emerald-800">{rupiah(b.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5DECF] overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]"><th className="p-3">Kasir (Email)</th><th className="p-3">Cabang</th><th className="p-3">Transaksi</th><th className="p-3">Omzet Kasir</th></tr></thead>
              <tbody>
                {cashierRows.map((c, i) => (
                  <tr key={c.email + i} className="border-b border-stone-100 last:border-0" data-testid={`report-cashier-${c.email}`}>
                    <td className="p-3 font-semibold">{c.email}</td>
                    <td className="p-3 text-stone-500">{c.branch_name}</td>
                    <td className="p-3">{c.pos_count} struk</td>
                    <td className="p-3 font-bold text-emerald-800">{rupiah(c.pos_revenue)}</td>
                  </tr>
                ))}
                {!cashierRows.length && (
                  <tr><td colSpan={4} className="p-3 text-stone-400 text-xs">Belum ada transaksi kasir pada tanggal ini.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfitReport({ onGoToProduct }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [month, setMonth] = useState(() => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 7));
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  const loadRep = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (branchId) params.set("branch_id", branchId);
    if (month) params.set("month", month);
    api.get(`/admin/profit-report?${params}`).then((r) => setRep(r.data)).catch(() => toast.error("Gagal memuat laporan laba")).finally(() => setLoading(false));
  };
  useEffect(loadRep, [month, branchId]);

  const dayMap = {};
  rep?.branches.forEach((b) => b.days.forEach((d) => {
    const cur = dayMap[d.date] || { revenue: 0, modal: 0, profit: 0, no_modal: 0 };
    dayMap[d.date] = { revenue: cur.revenue + d.revenue, modal: cur.modal + d.modal, profit: cur.profit + d.profit, no_modal: cur.no_modal + d.no_modal };
  }));
  const dayRows = Object.entries(dayMap).sort(([a], [bb]) => a.localeCompare(bb));
  const margin = rep && rep.grand_revenue > 0 ? (rep.grand_profit / rep.grand_revenue) * 100 : 0;
  const totalNoModal = rep?.branches.reduce((s, b) => s + b.no_modal, 0) || 0;
  const nmProducts = rep?.no_modal_products || [];
  const branchLabel = branchId ? (branches.find((b) => b.id === branchId)?.name || "") : "Semua Cabang";
  const printProfit = () => {
    document.body.classList.add("printing-profit");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-profit"), 500);
  };

  const isSuper = !user.branch_id;
  const [dayTx, setDayTx] = useState(null);
  const [dayTxLoading, setDayTxLoading] = useState(false);
  const [editTxId, setEditTxId] = useState(null);
  const [editRows, setEditRows] = useState([]);
  const [confirmDelId, setConfirmDelId] = useState(null);
  const [txBusy, setTxBusy] = useState(false);

  const openDay = async (date) => {
    if (!isSuper) return;
    setDayTx({ date, data: null });
    setDayTxLoading(true);
    setEditTxId(null); setConfirmDelId(null);
    try {
      const params = new URLSearchParams({ date });
      if (branchId) params.set("branch_id", branchId);
      const r = await api.get(`/admin/day-transactions?${params}`);
      setDayTx({ date, data: r.data });
    } catch { toast.error("Gagal memuat transaksi tanggal itu"); setDayTx(null); }
    finally { setDayTxLoading(false); }
  };
  const startEdit = (tx) => { setConfirmDelId(null); setEditTxId(tx.id); setEditRows(tx.items.map((it) => ({ product_id: it.product_id, name: it.name, price: it.price, qty: it.qty }))); };
  const editNewTotal = (tx) => editRows.reduce((s, r) => s + (Number(r.price) || 0) * (Number(r.qty) || 0), 0) + (tx.total - tx.item_omzet);
  const saveEdit = async (tx) => {
    const pin = await requestPin();
    if (!pin) return;
    setTxBusy(true);
    try {
      await api.patch(`/admin/pos-sales/${tx.id}?pin=${pin}`, { items: editRows.map((r) => ({ product_id: r.product_id, price: Number(r.price) || 0, qty: Number(r.qty) || 0 })) });
      toast.success("Transaksi diperbarui — stok otomatis disesuaikan");
      setEditTxId(null);
      openDay(dayTx.date);
      loadRep();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan perubahan"); }
    finally { setTxBusy(false); }
  };
  const doDeleteTx = async (tx) => {
    const pin = await requestPin();
    if (!pin) return;
    setTxBusy(true);
    try {
      await api.delete(`/admin/pos-sales/${tx.id}?pin=${pin}`);
      toast.success("Transaksi dihapus — stok dikembalikan");
      setConfirmDelId(null);
      if (editTxId === tx.id) setEditTxId(null);
      openDay(dayTx.date);
      loadRep();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus transaksi"); }
    finally { setTxBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="profit-report">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <h3 className="font-display font-bold text-xl">💰 Laba Kotor (Omzet Produk − Harga Modal)</h3>
        <div className="flex flex-wrap gap-2 items-center">
          <Input data-testid="profit-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
          {!user.branch_id && (
            <select data-testid="profit-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-2 bg-white">
              <option value="">Semua Cabang</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          {rep && (
            <button data-testid="profit-report-print-btn" onClick={printProfit} className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-bold flex items-center gap-1.5 hover:bg-emerald-700 transition"><Printer className="w-4 h-4" /> Cetak Laporan</button>
          )}
        </div>
      </div>
      {loading && <p className="text-sm text-stone-400">Memuat laporan laba...</p>}
      {rep && (
        <div id="profit-report-print" data-testid="profit-report-print">
          <div className="print-only" style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 900, fontSize: 18 }}>FASMART — Laporan Laba Kotor</div>
            <div style={{ fontSize: 12 }}>Periode: {rep.month} · Cabang: {branchLabel}</div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-stone-500">Omzet Produk</div>
              <div data-testid="profit-revenue" className="font-display font-black text-2xl text-stone-900 mt-1">{rupiah(rep.grand_revenue)}</div>
              <div className="text-xs text-stone-500">online selesai + kasir</div>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-stone-500">Harga Modal (HPP)</div>
              <div data-testid="profit-modal" className="font-display font-black text-2xl text-stone-900 mt-1">{rupiah(rep.grand_modal)}</div>
              <div className="text-xs text-stone-500">dari harga_modal produk</div>
            </div>
            <div className="bg-emerald-700 rounded-2xl p-4 text-white">
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-200">Laba Kotor</div>
              <div data-testid="profit-total" className="font-display font-black text-2xl mt-1">{rupiah(rep.grand_profit)}</div>
              <div className="text-xs text-emerald-200">{rep.month}</div>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-stone-500">Margin</div>
              <div data-testid="profit-margin" className="font-display font-black text-2xl text-emerald-800 mt-1">{margin.toFixed(1)}%</div>
              <div className="text-xs text-stone-500">laba ÷ omzet</div>
            </div>
          </div>
          {totalNoModal > 0 && (
            <div data-testid="profit-no-modal-warning" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              <p>⚠ {totalNoModal} item terjual belum punya harga modal — labanya terhitung penuh dari omzet. Lengkapi harga modal di tab Produk agar akurat.</p>
              {nmProducts.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 no-print">
                  {nmProducts.slice(0, 10).map((p) => (
                    <button key={p.id} type="button" data-testid={`profit-no-modal-go-${p.id}`} onClick={() => onGoToProduct?.(p.name)} title={`Buka "${p.name}" di tab Produk untuk mengisi harga modal`} className="px-2.5 py-1 rounded-full bg-amber-600 text-white text-[11px] font-bold hover:bg-amber-700 transition">{p.name} →</button>
                  ))}
                  {nmProducts.length > 10 && <span className="text-[11px] self-center font-semibold">+{nmProducts.length - 10} lainnya (cek tab 💲 Cek Modal)</span>}
                </div>
              )}
            </div>
          )}
          <div className="bg-white rounded-2xl border border-[#E5DECF] overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]"><th className="p-3">Cabang</th><th className="p-3">Omzet</th><th className="p-3">Modal</th><th className="p-3">Laba Kotor</th></tr></thead>
              <tbody>
                {rep.branches.map((b) => (
                  <tr key={b.branch_id} className="border-b border-stone-100 last:border-0" data-testid={`profit-row-${b.branch_id}`}>
                    <td className="p-3 font-semibold">{b.branch_name}</td>
                    <td className="p-3">{rupiah(b.revenue)}</td>
                    <td className="p-3">{rupiah(b.modal)}</td>
                    <td className="p-3 font-bold text-emerald-800">{rupiah(b.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isSuper && dayRows.length > 0 && <p className="text-[11px] text-stone-500">💡 Klik baris tanggal untuk cek & perbaiki transaksi hari itu (khusus super admin, wajib PIN).</p>}
          {dayRows.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E5DECF] overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]"><th className="p-3">Tanggal</th><th className="p-3">Omzet</th><th className="p-3">Modal</th><th className="p-3">Laba Kotor</th></tr></thead>
                <tbody>
                  {dayRows.map(([date, d]) => (
                    <tr key={date} onClick={() => openDay(date)} title={isSuper ? "Klik untuk cek & perbaiki transaksi hari ini" : undefined} className={`border-b border-stone-100 last:border-0 ${isSuper ? "cursor-pointer hover:bg-amber-50 transition" : ""}`} data-testid={`profit-day-${date}`}>
                      <td className="p-3 font-semibold">{new Date(date + "T00:00:00").toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}{isSuper && <span className="ml-1 text-[9px] text-stone-400">🔍</span>}</td>
                      <td className="p-3">{rupiah(d.revenue)}</td>
                      <td className="p-3">{rupiah(d.modal)}</td>
                      <td className={`p-3 font-bold ${d.profit < 0 ? "text-rose-700" : "text-emerald-800"}`}>{rupiah(d.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!dayRows.length && <p className="text-sm text-stone-400 bg-white rounded-2xl border border-[#E5DECF] p-4">Belum ada penjualan pada bulan ini.</p>}
        </div>
      )}
      {dayTx && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { setDayTx(null); setEditTxId(null); setConfirmDelId(null); }}>
          <div data-testid="daytx-modal" className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-black text-lg">🔍 Transaksi {new Date(dayTx.date + "T00:00:00").toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "long" })}</h4>
              <button data-testid="daytx-close" onClick={() => { setDayTx(null); setEditTxId(null); setConfirmDelId(null); }} className="w-8 h-8 rounded-full bg-stone-100 text-stone-500 font-black hover:bg-rose-100 hover:text-rose-600 transition">✕</button>
            </div>
            {dayTxLoading || !dayTx.data ? <p className="text-sm text-stone-400 py-8 text-center">Memuat transaksi…</p> : (
              <>
                <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                  <div className="bg-stone-50 rounded-xl p-2"><div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Omzet</div><div className="font-black text-sm" data-testid="daytx-omzet">{rupiah(dayTx.data.day_omzet)}</div></div>
                  <div className="bg-stone-50 rounded-xl p-2"><div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Modal</div><div className="font-black text-sm" data-testid="daytx-modal-val">{rupiah(dayTx.data.day_modal)}</div></div>
                  <div className="bg-stone-50 rounded-xl p-2"><div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Laba</div><div className={`font-black text-sm ${dayTx.data.day_profit < 0 ? "text-rose-700" : "text-emerald-800"}`} data-testid="daytx-profit">{rupiah(dayTx.data.day_profit)}</div></div>
                </div>
                {!dayTx.data.transactions.length && <p className="text-sm text-stone-400 text-center py-6">Tidak ada transaksi pada tanggal ini.</p>}
                {dayTx.data.transactions.map((tx) => (
                  <div key={tx.id} data-testid={`daytx-${tx.id}`} className="border border-[#E5DECF] rounded-xl p-3 mb-2.5">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-xs font-bold text-stone-700">{tx.time} · {tx.source === "kasir" ? "🧾 Kasir" : "🛒 Online"} · {tx.cashier}{tx.member_name ? ` · ${tx.member_name}` : ""} · {tx.branch_name}{tx.edited && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px]">✏️ pernah diedit</span>}</div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-black">{rupiah(tx.total)}</span>
                        {tx.source === "kasir" && editTxId !== tx.id && (
                          <>
                            <button data-testid={`daytx-edit-${tx.id}`} onClick={() => startEdit(tx)} className="px-2.5 py-1 rounded-full bg-stone-900 text-white text-[11px] font-bold hover:bg-emerald-700 transition">✏️ Edit</button>
                            <button data-testid={`daytx-del-${tx.id}`} onClick={() => { setEditTxId(null); setConfirmDelId(tx.id); }} className="px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold hover:bg-rose-600 hover:text-white transition">🗑 Hapus</button>
                          </>
                        )}
                      </div>
                    </div>
                    {editTxId === tx.id ? (
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                        <p className="text-[11px] font-bold text-amber-800 mb-1.5">Edit harga / qty item (qty 0 = keluarkan item) — total dihitung ulang & stok menyesuaikan otomatis:</p>
                        {editRows.map((r, i) => (
                          <div key={r.product_id} className="flex items-center gap-1.5 py-1">
                            <span className="flex-1 min-w-0 truncate text-xs font-semibold">{r.name}</span>
                            <Input type="number" min="0" value={r.price} data-testid={`daytx-edit-price-${r.product_id}`} onChange={(e) => setEditRows(editRows.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} className="w-24 h-8 text-xs text-right" title="Harga jual" />
                            <span className="text-xs text-stone-400">×</span>
                            <Input type="number" min="0" value={r.qty} data-testid={`daytx-edit-qty-${r.product_id}`} onChange={(e) => setEditRows(editRows.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} className="w-16 h-8 text-xs text-right" title="Qty" />
                          </div>
                        ))}
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-dashed border-amber-300">
                          <span className="text-xs">Total baru: <b data-testid="daytx-edit-newtotal" className="text-emerald-800">{rupiah(editNewTotal(tx))}</b></span>
                          <div className="flex gap-1.5">
                            <button data-testid="daytx-edit-cancel" onClick={() => setEditTxId(null)} className="px-3 py-1.5 rounded-full border border-[#E5DECF] text-stone-600 text-xs font-bold">Batal</button>
                            <button data-testid="daytx-edit-save" disabled={txBusy} onClick={() => saveEdit(tx)} className="px-3 py-1.5 rounded-full bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800 disabled:opacity-50">Simpan (PIN)</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-1.5 divide-y divide-dashed divide-stone-100">
                        {tx.items.map((it, i) => (
                          <div key={i} className={`py-1 text-[11px] flex items-center justify-between gap-2 ${it.loss ? "bg-rose-50 text-rose-700 font-bold rounded px-1 -mx-1" : ""}`} data-testid={it.loss ? `daytx-loss-${tx.id}-${i}` : undefined}>
                            <span className="flex-1 min-w-0 truncate">{it.name} × {it.qty} @ {rupiah(it.price)}</span>
                            <span className="shrink-0 text-stone-500">modal {rupiah(it.modal)}</span>
                            <span className={`shrink-0 w-20 text-right font-bold ${it.laba < 0 ? "text-rose-700" : "text-emerald-800"}`}>{it.laba >= 0 ? "+" : ""}{rupiah(it.laba)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {confirmDelId === tx.id && (
                      <div className="mt-2 bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs" data-testid={`daytx-delbox-${tx.id}`}>
                        <p className="font-bold text-rose-800 mb-2">Yakin hapus transaksi ini? Stok {tx.items.reduce((s, it) => s + it.qty, 0)} pcs dikembalikan & penghapusan tercatat di log audit.</p>
                        <div className="flex gap-1.5 justify-end">
                          <button onClick={() => setConfirmDelId(null)} className="px-3 py-1.5 rounded-full border border-[#E5DECF] text-stone-600 text-xs font-bold">Batal</button>
                          <button data-testid={`daytx-del-confirm-${tx.id}`} disabled={txBusy} onClick={() => doDeleteTx(tx)} className="px-3 py-1.5 rounded-full bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 disabled:opacity-50">Ya, Hapus (PIN)</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StockOpnameTab() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [physical, setPhysical] = useState("");
  const [logs, setLogs] = useState([]);
  const [showScanner, setShowScanner] = useState(false);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data.filter((p) => FASMART_CATS.includes(p.category)))).catch(() => {});
  useEffect(() => {
    api.get("/branches").then((r) => { setBranches(r.data); if (r.data.length) setBranchId(r.data[0].id); }).catch(() => {});
    loadProducts();
  }, []);
  const loadLogs = () => api.get(`/admin/stock-opname/logs${branchId ? `?branch_id=${branchId}` : ""}`).then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { loadLogs(); }, [branchId]);

  const findExact = (q) => {
    const n = q.trim().toLowerCase();
    if (!n) return null;
    return products.find((p) => (p.barcode && String(p.barcode).toLowerCase() === n) || p.id.toLowerCase() === n) || null;
  };
  const suggestions = (() => {
    const q = query.trim().toLowerCase();
    if (!q || findExact(q)) return [];
    return products.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 6);
  })();

  const pick = (p) => { setSelected(p); setPhysical(""); setQuery(""); };
  const onQueryEnter = (e) => {
    if (e.key !== "Enter") return;
    const p = findExact(query) || (suggestions.length === 1 ? suggestions[0] : null);
    if (p) pick(p);
  };
  const handleScan = (code) => {
    setShowScanner(false);
    const p = findExact(code);
    if (p) { pick(p); toast.success(p.name); }
    else toast.error(`Barcode ${code} tidak ditemukan`);
  };

  const sysStock = selected ? (selected.stock_by_branch?.[branchId] ?? 0) : 0;
  const diff = physical !== "" ? parseInt(physical, 10) - sysStock : null;

  const save = async () => {
    if (!selected || physical === "") return;
    try {
      await api.post("/admin/stock-opname", { product_id: selected.id, branch_id: branchId, physical_stock: parseInt(physical, 10) });
      toast.success(`Stok fisik tersimpan: ${selected.name}`);
      setSelected(null);
      setPhysical("");
      loadLogs();
      loadProducts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  return (
    <div className="space-y-4" data-testid="stock-opname-tab">
      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <select data-testid="opname-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-2 bg-white">
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div className="flex gap-2 mt-3">
          <div className="relative flex-1">
            <input
              data-testid="opname-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onQueryEnter}
              placeholder="Scan barcode / ketik nama produk..."
              className="w-full px-4 py-2.5 rounded-xl border border-[#E5DECF] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/50"
            />
            {query && <button data-testid="opname-search-clear" onClick={() => setQuery("")} title="Hapus pencarian/barcode" className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 text-stone-500 hover:bg-rose-100 hover:text-rose-600 flex items-center justify-center transition"><X className="w-3 h-3" /></button>}
            {suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5DECF] rounded-xl shadow-lg z-10 max-h-56 overflow-y-auto">
                {suggestions.map((p) => (
                  <button key={p.id} data-testid={`opname-suggest-${p.id}`} onClick={() => pick(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-stone-100 last:border-0">{p.name}</button>
                ))}
              </div>
            )}
          </div>
          <button data-testid="opname-scan-btn" onClick={() => setShowScanner(true)} className="shrink-0 px-3.5 rounded-xl bg-stone-900 text-white text-sm font-bold hover:bg-emerald-700 transition">📷</button>
        </div>

        {selected && (
          <div data-testid="opname-selected" className="mt-4 rounded-xl border-2 border-emerald-600 bg-emerald-50/50 p-4">
            <div className="font-bold text-sm text-stone-900">{selected.name}</div>
            <div className="text-xs text-stone-500 mt-0.5">{selected.category} · satuan {selected.unit}</div>
            <div className="flex flex-wrap items-end gap-4 mt-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Stok Sistem</div>
                <div data-testid="opname-sys-stock" className="font-display font-black text-2xl text-stone-900">{sysStock}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Stok Fisik (hitungan rak)</div>
                <input data-testid="opname-physical-input" type="number" value={physical} onChange={(e) => setPhysical(e.target.value)} className="w-28 border border-[#E5DECF] rounded-xl px-3 py-2 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-emerald-600/50" placeholder="0" />
              </div>
              {diff !== null && !isNaN(diff) && (
                <div data-testid="opname-diff" className={`text-sm font-black ${diff === 0 ? "text-emerald-700" : "text-rose-600"}`}>
                  Selisih: {diff > 0 ? `+${diff}` : diff}
                </div>
              )}
              <button data-testid="opname-save-btn" onClick={save} disabled={physical === ""} className="ml-auto px-5 py-2.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold transition disabled:opacity-50">Simpan Stok Fisik</button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-black text-base">Riwayat Opname Terakhir</h3>
          <button onClick={loadLogs} className="text-xs text-emerald-700 font-bold">Refresh</button>
        </div>
        <div data-testid="opname-logs" className="space-y-1.5 max-h-64 overflow-y-auto">
          {logs.length === 0 && <p className="text-xs text-stone-400">Belum ada catatan opname.</p>}
          {logs.map((l) => (
            <div key={l.id} className="flex items-center justify-between text-xs border-b border-dashed pb-1.5">
              <span className="truncate mr-2"><b>{l.product_name}</b> <span className="text-stone-400">· {new Date(l.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })}</span></span>
              <span className={`shrink-0 font-bold ${l.new_stock === l.old_stock ? "text-stone-500" : "text-rose-600"}`}>{l.old_stock} → {l.new_stock}</span>
            </div>
          ))}
        </div>
      </div>

      {showScanner && <BarcodeScanner onResult={handleScan} onClose={() => setShowScanner(false)} />}
    </div>
  );
}

function BranchBadge({ branchId }) {
  const [name, setName] = useState("");
  useEffect(() => {
    if (!branchId) return;
    api.get("/branches").then((r) => {
      const b = r.data.find((x) => x.id === branchId);
      setName(b ? b.name : "");
    }).catch(() => {});
  }, [branchId]);
  if (!branchId || !name) return null;
  return <span data-testid="admin-branch-badge" className="tag-pill bg-amber-100 text-amber-800">🏪 Cabang: {name}</span>;
}

export default function Admin() {
  const { user } = useAuth();
  const { newCount: newOrders, refresh: refreshOrders } = useNewOrderAlert();
  const [tab, setTab] = useState("overview");
  const [prodFocus, setProdFocus] = useState("");
  const goToProduct = (name) => { setProdFocus(name); setTab("products"); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (!user) {
    return (
      <div className="min-h-screen paper-bg">
        <Navbar />
        <div className="max-w-lg mx-auto text-center py-24">
          <h2 className="font-display font-black text-3xl">Akses admin</h2>
          <p className="mt-2 text-stone-600">Silakan masuk sebagai admin toko.</p>
        </div>
      </div>
    );
  }
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen paper-bg">
        <Navbar />
        <div className="max-w-lg mx-auto text-center py-24">
          <h2 className="font-display font-black text-3xl">Bukan admin</h2>
          <p className="mt-2 text-stone-600">Halaman ini khusus untuk pemilik warung.</p>
        </div>
      </div>
    );
  }

  if (user.input_only) {
    return (
      <div className="min-h-screen paper-bg">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">Input Produk</h1>
              <p className="text-stone-600 mt-1">Mode staff input — kelola barcode, harga beli/modal, harga jual & stok produk.</p>
            </div>
            <span className="tag-pill bg-emerald-100 text-emerald-800">Staff: {user.email}</span>
          </div>
          <ProductsTab />
        </div>
        <PinGateModal />
      </div>
    );
  }

  return (
    <div className="min-h-screen paper-bg">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">Kasir Toko</h1>
            <p className="text-stone-600 mt-1">Kelola pesanan, produk, dan pelanggan Toko FASMART.</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {!user.branch_id && !user.input_only && <NotificationBell />}
            <span className="tag-pill bg-emerald-100 text-emerald-800">Admin: {user.email}</span>
            <BranchBadge branchId={user.branch_id} />
            <a href="/kasir" data-testid="admin-kasir-link" className="tag-pill bg-stone-900 text-white hover:bg-emerald-700 transition">🧾 Kasir Toko →</a>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="bg-white border border-[#E5DECF] rounded-full p-1 h-auto flex flex-wrap">
            <TabsTrigger data-testid="admin-tab-overview" value="overview" className="rounded-full px-4 py-2">Ringkasan</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-orders" value="orders" className="rounded-full px-4 py-2">
              Pesanan
              {newOrders > 0 && (
                <span data-testid="admin-new-orders-badge" className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-rose-600 text-white text-[10px] font-black">{newOrders}</span>
              )}
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-products" value="products" className="rounded-full px-4 py-2">Produk</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-customers" value="customers" className="rounded-full px-4 py-2">Pelanggan</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-laporan" value="laporan" className="rounded-full px-4 py-2">📊 Laporan</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-keuangan" value="keuangan" className="rounded-full px-4 py-2">💰 Keuangan</TabsTrigger>
            {!user.branch_id && <TabsTrigger data-testid="admin-tab-audit" value="audit" className="rounded-full px-4 py-2">🔍 Audit</TabsTrigger>}
            {!user.branch_id && <TabsTrigger data-testid="admin-tab-branches" value="branches" className="rounded-full px-4 py-2">🏪 Cabang</TabsTrigger>}
            {!user.branch_id && <TabsTrigger data-testid="admin-tab-opname" value="opname" className="rounded-full px-4 py-2">📦 Stok Opname</TabsTrigger>}
            {!user.branch_id && !user.input_only && <TabsTrigger data-testid="admin-tab-pricing" value="pricing" className="rounded-full px-4 py-2">💲 Cek Modal</TabsTrigger>}
            {!user.branch_id && !user.input_only && <TabsTrigger data-testid="admin-tab-backup" value="backup" className="rounded-full px-4 py-2">💾 Backup</TabsTrigger>}
            {!user.input_only && <TabsTrigger data-testid="admin-tab-printer" value="printer" className="rounded-full px-4 py-2">🖨 Printer</TabsTrigger>}
            {!user.branch_id && !user.input_only && <TabsTrigger data-testid="admin-tab-restock" value="restock" className="rounded-full px-4 py-2">🛒 Belanja</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="orders"><OrdersTab onChanged={refreshOrders} /></TabsContent>
          <TabsContent value="products"><ProductsTab focusSearch={prodFocus} onFocusDone={() => setProdFocus("")} /></TabsContent>
          <TabsContent value="customers"><CustomersTab /></TabsContent>
          <TabsContent value="laporan"><DailyReportTab /><div className="mt-8"><TransactionReport /></div><div className="mt-8"><ProfitReport onGoToProduct={goToProduct} /></div></TabsContent>
          <TabsContent value="keuangan"><FinanceTab /></TabsContent>
          {!user.branch_id && <TabsContent value="audit"><AuditTab /></TabsContent>}
          {!user.branch_id && <TabsContent value="branches"><BranchesTab /></TabsContent>}
          {!user.branch_id && <TabsContent value="opname"><StockOpnameTab /></TabsContent>}
          {!user.branch_id && !user.input_only && <TabsContent value="pricing"><PricingCheckTab /></TabsContent>}
          {!user.branch_id && !user.input_only && <TabsContent value="backup"><BackupTab /></TabsContent>}
          {!user.input_only && <TabsContent value="printer"><PrinterTab /></TabsContent>}
          {!user.branch_id && !user.input_only && <TabsContent value="restock"><RestockList /></TabsContent>}
          <PinGateModal />
        </Tabs>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [waClicks, setWaClicks] = useState([]);
  const [waWeekly, setWaWeekly] = useState([]);
  const [salesOpen, setSalesOpen] = useState(false);
  const [salesOrders, setSalesOrders] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", notes: "" });

  const ACTIVE_STATUSES = ["baru", "cek_transfer", "diterima", "disiapkan", "akan_dikirim", "dikirim"];
  const activeOrders = allOrders.filter((o) => ACTIVE_STATUSES.includes(o.status));

  const custInsights = {};
  allOrders.forEach((o) => {
    const k = o.user_id;
    if (!custInsights[k]) custInsights[k] = { count: 0, total: 0, items: {}, phone: "" };
    custInsights[k].count += 1;
    custInsights[k].total += o.total;
    if (!custInsights[k].phone && o.phone) custInsights[k].phone = o.phone;
    (o.items || []).forEach((it) => { custInsights[k].items[it.name] = (custInsights[k].items[it.name] || 0) + it.qty; });
  });
  Object.values(custInsights).forEach((v) => {
    v.topItems = Object.entries(v.items).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([n]) => n);
  });

  const saveCustomer = async (uid) => {
    try {
      await api.patch(`/admin/customers/${uid}`, editForm);
      toast.success("Data pelanggan diperbarui");
      setEditing(null);
      api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    }
  };
  const [salesFilter, setSalesFilter] = useState("semua");

  const filteredSales = salesOrders.filter((o) => {
    if (salesFilter === "semua") return true;
    const d = new Date(o.created_at);
    const now = new Date();
    if (salesFilter === "hari_ini") return d.toDateString() === now.toDateString();
    if (salesFilter === "minggu") {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      start.setHours(0, 0, 0, 0);
      return d >= start;
    }
    if (salesFilter === "bulan") return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    return true;
  });

  const exportCsv = () => {
    const rows = [["ID Pesanan", "Tanggal", "Pelanggan", "Metode Bayar", "Total"]];
    filteredSales.forEach((o) => rows.push([o.id, new Date(o.created_at).toLocaleString("id-ID"), o.customer_name, (o.payment_method || "cod").toUpperCase(), o.total]));
    rows.push(["", "", "", "TOTAL", filteredSales.reduce((a, o) => a + o.total, 0)]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `penjualan-${salesFilter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/analytics/wa-clicks").then((r) => setWaClicks(r.data)).catch(() => {});
    api.get("/analytics/wa-clicks-weekly").then((r) => setWaWeekly(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!dialog) return;
    api.get("/orders").then((r) => {
      setAllOrders(r.data);
      setSalesOrders(r.data.filter((o) => o.status === "selesai"));
    }).catch(() => {});
    if (dialog === "customers") api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    if (dialog === "products") api.get("/products").then((r) => setAllProducts(r.data)).catch(() => {});
  }, [dialog]);
  if (!stats) return <div className="mt-6 text-stone-500">Memuat...</div>;

  const cards = [
    { label: "Total Penjualan", value: rupiah(stats.total_revenue), icon: DollarSign, color: "bg-emerald-700 text-white" },
    { label: "Order Aktif", value: stats.active_orders, icon: ShoppingBag, color: "bg-orange-500 text-white" },
    { label: "Order Selesai", value: stats.completed_orders, icon: TrendingUp, color: "bg-amber-400 text-emerald-950" },
    { label: "Pelanggan", value: stats.total_customers, icon: Users, color: "bg-stone-900 text-white" },
    { label: "Total Produk", value: stats.total_products, icon: Package, color: "bg-white text-stone-900 border border-[#E5DECF]" },
  ];

  return (
    <div className="mt-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((c, i) => (
          <div
            key={i}
            data-testid={`admin-stat-${i}`}
            onClick={() => setDialog(["sales", "active", "done", "customers", "products"][i])}
            title="Klik untuk rincian"
            className={`rounded-3xl p-5 ${c.color} cursor-pointer hover:brightness-110 transition`}
          >
            <c.icon className="w-5 h-5 opacity-80" />
            <div className="mt-3 text-xs font-mono uppercase tracking-widest opacity-80">{c.label}</div>
            <div className="mt-1 font-display font-black text-2xl">{c.value}</div>
            {["sales", "active", "done", "customers", "products"][i] && <div className="mt-1 text-[10px] font-semibold opacity-80 underline">Klik untuk rincian →</div>}
          </div>
        ))}
      </div>
      <div className="mt-6 bg-white rounded-3xl border border-[#E5DECF] p-6">
        <h3 className="font-display font-bold text-xl">Top Produk Warung</h3>
        <div className="mt-4 space-y-2">
          {stats.top_products.length === 0 && <p className="text-sm text-stone-500">Belum ada penjualan.</p>}
          {stats.top_products.map((p, i) => (
            <div key={p.name} className="flex items-center justify-between rounded-xl bg-stone-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-emerald-700 text-white text-xs font-black flex items-center justify-center">#{i + 1}</span>
                <span className="font-semibold">{p.name}</span>
              </div>
              <span className="tag-pill bg-amber-100 text-amber-800">{p.qty} terjual</span>
            </div>
          ))}
        </div>
      </div>
      <AnalyticsSection />

      {dialog === "sales" && (
        <div data-testid="admin-sales-dialog" className="fixed inset-0 z-50 bg-stone-950/60 flex items-center justify-center p-4" onClick={() => setDialog(null)}>
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-black text-xl">Rincian Total Penjualan</h3>
              <button data-testid="admin-sales-close" onClick={() => setDialog(null)} className="text-stone-400 hover:text-stone-700 text-xl font-bold px-2">×</button>
            </div>
            <p className="text-xs text-stone-500 mt-1">Dari {filteredSales.length} pesanan berstatus Selesai.</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {[["semua", "Semua"], ["hari_ini", "Hari Ini"], ["minggu", "Minggu Ini"], ["bulan", "Bulan Ini"]].map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`admin-sales-filter-${k}`}
                  onClick={() => setSalesFilter(k)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition ${salesFilter === k ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-[#E5DECF] hover:border-emerald-600"}`}
                >
                  {label}
                </button>
              ))}
              <button
                data-testid="admin-sales-export-btn"
                onClick={exportCsv}
                disabled={filteredSales.length === 0}
                className="ml-auto px-3.5 py-1.5 rounded-full text-xs font-bold bg-stone-900 text-white hover:bg-stone-700 transition disabled:opacity-40"
              >
                ⬇ Ekspor CSV
              </button>
            </div>
            <div className="space-y-2 mt-3">
              {filteredSales.length === 0 && <p className="text-sm text-stone-500">Tidak ada pesanan selesai pada periode ini.</p>}
              {filteredSales.map((o) => (
                <div key={o.id} data-testid={`admin-sales-row-${o.id}`} className="flex items-center justify-between rounded-xl bg-stone-50 px-4 py-3 text-sm">
                  <div>
                    <div className="font-semibold">#{o.id.slice(-6).toUpperCase()} · {o.customer_name}</div>
                    <div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleString("id-ID")} · {(o.payment_method || "cod").toUpperCase()}</div>
                  </div>
                  <div className="font-display font-bold text-emerald-800">{rupiah(o.total)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[#E5DECF] flex items-center justify-between font-display font-black text-lg">
              <span>Total Penjualan</span>
              <span data-testid="admin-sales-total" className="text-emerald-800">{rupiah(filteredSales.reduce((a, o) => a + o.total, 0))}</span>
            </div>
          </div>
        </div>
      )}

      {dialog === "active" && (
        <div data-testid="admin-active-dialog" className="fixed inset-0 z-50 bg-stone-950/60 flex items-center justify-center p-4" onClick={() => setDialog(null)}>
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-black text-xl">Order Aktif ({activeOrders.length})</h3>
              <button data-testid="admin-active-close" onClick={() => setDialog(null)} className="text-stone-400 hover:text-stone-700 text-xl font-bold px-2">×</button>
            </div>
            <div className="space-y-2 mt-3">
              {activeOrders.length === 0 && <p className="text-sm text-stone-500">Tidak ada order aktif.</p>}
              {activeOrders.map((o) => (
                <div key={o.id} data-testid={`admin-active-row-${o.id}`} className="flex items-center justify-between rounded-xl bg-stone-50 px-4 py-3 text-sm">
                  <div>
                    <div className="font-semibold">#{o.id.slice(-6).toUpperCase()} · {o.customer_name}</div>
                    <div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleString("id-ID")}</div>
                  </div>
                  <div className="text-right">
                    <span className={`tag-pill border ${statusInfo(o.status).color}`}>{statusInfo(o.status).label}</span>
                    <div className="font-display font-bold text-emerald-800 mt-1">{rupiah(o.total)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {dialog === "done" && (
        <div data-testid="admin-done-dialog" className="fixed inset-0 z-50 bg-stone-950/60 flex items-center justify-center p-4" onClick={() => setDialog(null)}>
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-black text-xl">Order Selesai ({salesOrders.length})</h3>
              <button data-testid="admin-done-close" onClick={() => setDialog(null)} className="text-stone-400 hover:text-stone-700 text-xl font-bold px-2">×</button>
            </div>
            <div className="space-y-2 mt-3">
              {salesOrders.length === 0 && <p className="text-sm text-stone-500">Belum ada order selesai.</p>}
              {salesOrders.map((o) => (
                <div key={o.id} data-testid={`admin-done-row-${o.id}`} className="flex items-center justify-between rounded-xl bg-stone-50 px-4 py-3 text-sm">
                  <div>
                    <div className="font-semibold">#{o.id.slice(-6).toUpperCase()} · {o.customer_name}</div>
                    <div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleString("id-ID")}</div>
                  </div>
                  <div className="font-display font-bold text-emerald-800">{rupiah(o.total)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {dialog === "products" && (
        <div data-testid="admin-products-dialog" className="fixed inset-0 z-50 bg-stone-950/60 flex items-center justify-center p-4" onClick={() => setDialog(null)}>
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-black text-xl">Total Produk ({allProducts.length})</h3>
              <button data-testid="admin-products-close" onClick={() => setDialog(null)} className="text-stone-400 hover:text-stone-700 text-xl font-bold px-2">×</button>
            </div>
            <div className="space-y-2 mt-3">
              {allProducts.map((p) => (
                <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center justify-between rounded-xl bg-stone-50 px-4 py-2.5 text-sm">
                  <div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-stone-500">{p.category} · stok {p.stock}</div>
                  </div>
                  <div className="font-display font-bold text-emerald-800">{rupiah(p.price)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {dialog === "customers" && (
        <div data-testid="admin-customers-dialog" className="fixed inset-0 z-50 bg-stone-950/60 flex items-center justify-center p-4" onClick={() => setDialog(null)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-black text-xl">Data Pelanggan ({customers.length})</h3>
              <button data-testid="admin-customers-close" onClick={() => setDialog(null)} className="text-stone-400 hover:text-stone-700 text-xl font-bold px-2">×</button>
            </div>
            <p className="text-xs text-stone-500 mt-1">Lihat kebiasaan belanja & kirim promo langsung via WhatsApp.</p>
            <div className="space-y-3 mt-3">
              {customers.map((c) => {
                const ins = custInsights[c.user_id] || { count: 0, total: 0, topItems: [], phone: "" };
                const rawPhone = c.phone || ins.phone || "";
                const waNum = rawPhone.replace(/\D/g, "").replace(/^0/, "62");
                return (
                  <div key={c.user_id} data-testid={`admin-cust-${c.user_id}`} className="rounded-2xl bg-stone-50 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="font-display font-bold">{c.name} <span className="tag-pill bg-amber-100 text-amber-800 ml-1">{c.tier}</span></div>
                        <div className="text-xs text-stone-500">{c.email}{rawPhone ? ` · ${rawPhone}` : ""}</div>
                        <div className="text-xs text-stone-600 mt-1">
                          {ins.count} pesanan · total {rupiah(ins.total)} · {c.poin_berkah} poin
                        </div>
                        {ins.topItems?.length > 0 && (
                          <div className="text-xs text-stone-500 mt-0.5">Sering beli: <b>{ins.topItems.join(", ")}</b></div>
                        )}
                        {c.notes && <div className="text-xs text-violet-700 mt-0.5">Catatan: {c.notes}</div>}
                      </div>
                      <div className="flex gap-2">
                        {waNum && (
                          <a
                            data-testid={`admin-cust-wa-${c.user_id}`}
                            href={`https://wa.me/${waNum}?text=${encodeURIComponent(`Halo ${c.name}! Ada promo menarik dari FASMART untuk Anda. Cek di fasmart.co ya!`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition"
                          >
                            <MessageCircle className="w-3.5 h-3.5" /> WA Promo
                          </a>
                        )}
                        <button
                          data-testid={`admin-cust-edit-${c.user_id}`}
                          onClick={() => { setEditing(editing === c.user_id ? null : c.user_id); setEditForm({ name: c.name, phone: rawPhone, notes: c.notes || "" }); }}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-stone-900 text-white text-xs font-bold hover:bg-stone-700 transition"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                      </div>
                    </div>
                    {editing === c.user_id && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 border-t border-stone-200 pt-3">
                        <Input data-testid={`admin-cust-name-${c.user_id}`} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Nama" className="bg-white" />
                        <Input data-testid={`admin-cust-phone-${c.user_id}`} value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="No. HP/WA" className="bg-white" />
                        <Input data-testid={`admin-cust-notes-${c.user_id}`} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Catatan (kebiasaan, keperluan)" className="bg-white" />
                        <button data-testid={`admin-cust-save-${c.user_id}`} onClick={() => saveCustomer(c.user_id)} className="sm:col-span-3 px-4 py-2 rounded-full bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800 transition w-fit">
                          Simpan Perubahan
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalyticsSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const maxHour = Math.max(...data.orders_by_hour, 1);
  const cards = [
    { label: "Kunjungan Hari Ini", value: data.visits_today, testid: "analytics-visits-today" },
    { label: "Total Kunjungan", value: data.total_visits, testid: "analytics-total-visits" },
    { label: "Pengunjung Unik", value: data.unique_visitors, testid: "analytics-unique-visitors" },
    { label: "Total Login", value: data.total_logins, testid: "analytics-logins" },
    { label: "Pelanggan Terdaftar", value: data.customers, testid: "analytics-customers" },
  ];
  return (
    <div className="mt-6 bg-white rounded-3xl border border-[#E5DECF] p-6" data-testid="admin-analytics-card">
      <h3 className="font-display font-bold text-xl">Analitik Pengunjung & Pola Belanja</h3>
      <p className="text-xs text-stone-500 mt-1">Tracking kunjungan mulai tercatat sejak fitur ini aktif — data makin akurat seiring waktu.</p>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="rounded-2xl bg-stone-50 px-4 py-3">
            <div className="font-display font-black text-2xl text-emerald-800">{c.value}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <div>
          <div className="text-sm font-bold mb-2">Jam Ramai Belanja (WIB)</div>
          <div data-testid="analytics-hours-chart" className="flex items-end gap-1 h-28">
            {data.orders_by_hour.map((n, h) => (
              <div key={h} className="flex-1 flex flex-col items-center justify-end h-full" title={`Jam ${h}.00: ${n} pesanan`}>
                {n > 0 && <div className="text-[9px] font-bold text-stone-600">{n}</div>}
                <div className={`w-full rounded-t ${n > 0 ? "bg-emerald-600" : "bg-stone-100"}`} style={{ height: `${Math.max(4, (n / maxHour) * 100)}%` }} />
                {h % 3 === 0 && <div className="text-[8px] font-mono text-stone-400 mt-0.5">{h}</div>}
              </div>
            ))}
          </div>
          {data.orders_by_hour.every((n) => n === 0) && <p className="text-xs text-stone-400 mt-2">Belum ada pesanan tercatat.</p>}
        </div>
        <div>
          <div className="text-sm font-bold mb-2">Slot Pengantaran Terpopuler</div>
          <div data-testid="analytics-slots" className="space-y-1.5">
            {data.top_slots.length === 0 && <p className="text-xs text-stone-400">Belum ada pesanan tercatat.</p>}
            {data.top_slots.map(([slot, n]) => (
              <div key={slot} className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2 text-xs">
                <span className="font-semibold">{slot === "sekarang" ? "Kirim Sekarang (20-30 mnt)" : slot}</span>
                <span className="tag-pill bg-emerald-100 text-emerald-800">{n}x</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm font-bold mb-2">Pencarian Terpopuler</div>
          <div data-testid="analytics-searches" className="space-y-1.5">
            {(data.top_searches || []).length === 0 && <p className="text-xs text-stone-400">Belum ada pencarian tercatat.</p>}
            {(data.top_searches || []).map(([kw, n]) => (
              <div key={kw} className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2 text-xs">
                <span className="font-semibold capitalize">{kw}</span>
                <span className="tag-pill bg-sky-100 text-sky-800">{n}x</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6">
        <div className="text-sm font-bold mb-2">Alamat Pelanggan Terbanyak</div>
        <div data-testid="analytics-addresses" className="space-y-1.5">
          {data.top_addresses.length === 0 && <p className="text-xs text-stone-400">Belum ada alamat tercatat.</p>}
          {data.top_addresses.map(([addr, n]) => (
            <div key={addr} className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2 text-xs">
              <span className="truncate mr-2">{addr}</span>
              <span className="tag-pill bg-amber-100 text-amber-800 shrink-0">{n}x</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const WA_BRAND_COLORS = {
  home: "bg-emerald-600", music: "bg-amber-500", kost: "bg-orange-600",
  fasnet: "bg-blue-600", printing: "bg-sky-500", indotravel: "bg-rose-500", trading: "bg-stone-700",
};

function WeeklyWaChart({ data }) {
  if (!data.length) return null;
  const brands = [...new Set(data.flatMap((w) => Object.keys(w).filter((k) => k !== "week")))];
  const totals = data.map((w) => brands.reduce((a, b) => a + (w[b] || 0), 0));
  const maxTotal = Math.max(...totals, 1);
  return (
    <div className="mt-6 bg-white rounded-3xl border border-[#E5DECF] p-6" data-testid="admin-wa-weekly-chart">
      <h3 className="font-display font-bold text-xl">Tren Klik WA per Minggu</h3>
      <p className="text-xs text-stone-500 mt-1">8 minggu terakhir — pantau efek promo per periode.</p>
      <div className="mt-5 flex items-end gap-2 sm:gap-3 h-44 overflow-x-auto pb-1">
        {data.map((w, i) => {
          const total = totals[i];
          return (
            <div key={w.week} className="flex flex-col items-center justify-end h-full min-w-[52px] flex-1">
              <div className="text-xs font-bold text-stone-700 mb-1">{total}</div>
              <div className="w-full max-w-[48px] flex flex-col-reverse rounded-t-lg overflow-hidden" style={{ height: `${Math.max(6, (total / maxTotal) * 100)}%` }}>
                {brands.map((b) =>
                  w[b] ? (
                    <div
                      key={b}
                      className={`${WA_BRAND_COLORS[b] || "bg-stone-400"} w-full`}
                      style={{ height: `${(w[b] / total) * 100}%` }}
                      title={`${b}: ${w[b]} klik`}
                    />
                  ) : null
                )}
              </div>
              <div className="mt-1.5 text-[10px] font-mono text-stone-500">{new Date(w.week).toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit" })}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {brands.map((b) => (
          <span key={b} className="inline-flex items-center gap-1.5 text-xs text-stone-600">
            <span className={`w-2.5 h-2.5 rounded-full ${WA_BRAND_COLORS[b] || "bg-stone-400"}`} />
            <span className="capitalize">{b}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function OrdersTab({ onChanged }) {
  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState([]);
  const [updating, setUpdating] = useState(null);
  const [proofs, setProofs] = useState({});
  const [uploading, setUploading] = useState(null);
  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "";

  const updateStatus = async (id, status, proof) => {
    setUpdating(id);
    try {
      await api.patch(`/orders/${id}/status`, { status, ...(proof ? { handover_photo_url: proof } : {}) });
      toast.success(`Status pesanan diperbarui: ${statusInfo(status).label}`);
      load();
      onChanged?.();
    } catch { toast.error("Gagal update status"); }
    finally { setUpdating(null); }
  };

  const uploadProof = async (id, file) => {
    if (!file) return;
    setUploading(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/design", fd);
      setProofs((p) => ({ ...p, [id]: r.data.path }));
      toast.success("Bukti serah terima terupload");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal upload bukti");
    } finally { setUploading(null); }
  };

  const changeStatus = (o, next) => {
    if (next === "diterima") {
      if (!window.confirm("Crosscheck dulu: pastikan pembayaran/transferan konsumen SUDAH MASUK & BERHASIL sebelum menerima pesanan. Lanjutkan?")) return;
    }
    if (next === "selesai") {
      const proof = proofs[o.id] || o.handover_photo_url;
      if (!proof) {
        toast.error("Upload bukti foto serah terima kurir ke konsumen dulu");
        return;
      }
      return updateStatus(o.id, next, proof);
    }
    updateStatus(o.id, next);
  };

  return (
    <div className="mt-6 space-y-3">
      {orders.length === 0 && <p className="text-sm text-stone-500">Belum ada pesanan masuk.</p>}
      {orders.map((o) => {
        const st = statusInfo(o.status);
        return (
          <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white rounded-3xl border border-[#E5DECF] p-5">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">#{o.id.slice(-6).toUpperCase()} · {new Date(o.created_at).toLocaleString("id-ID")}</div>
                <div className="font-display font-bold text-lg">{o.customer_name} · {o.phone}</div>
                <div className="text-xs text-stone-600 max-w-lg">{o.delivery_type === "antar" ? `Antar ke: ${o.address}` : "Diambil di warung"}</div>
                {o.payment_method === "transfer" && o.transfer_amount > 0 && (
                  <div data-testid={`admin-order-transfer-${o.id}`} className="text-xs font-bold text-sky-700 mt-1">🏦 Transfer BRI: {rupiah(o.transfer_amount)} · kode unik <span className="font-mono">{String(o.transfer_code).padStart(3, "0")}</span></div>
                )}
                {o.notes && <div className="text-xs text-stone-500 mt-1">Catatan: {o.notes}</div>}
                {o.referral_name && (
                  <div data-testid={`admin-order-referral-${o.id}`} className="text-xs text-orange-600 font-semibold mt-1">
                    🎁 Diajak oleh: {o.referral_name} — siapkan hadiah ½ kg gula + 5 Indomie kaldu
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`tag-pill border ${st.color}`}>{st.label}</span>
                {(() => {
                  const idx = ORDER_FLOW.indexOf(o.status);
                  if (idx === -1) return null;
                  const nextOpts = ORDER_FLOW.slice(idx + 1).map((k) => ({ key: k, label: NEXT_STATUS_LABEL[k] }));
                  nextOpts.push({ key: "dibatalkan", label: NEXT_STATUS_LABEL.dibatalkan });
                  return (
                    <Select value="" onValueChange={(v) => changeStatus(o, v)} disabled={updating === o.id}>
                      <SelectTrigger data-testid={`admin-order-action-${o.id}`} className="w-56">
                        <SelectValue placeholder="Ubah status…" />
                      </SelectTrigger>
                      <SelectContent>
                        {nextOpts.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
            </div>
            {o.status === "dikirim" && (
              <div className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl bg-violet-50 border border-violet-200 px-4 py-3">
                <span className="text-xs font-semibold text-violet-800">Bukti foto serah terima kurir → konsumen (wajib untuk menyelesaikan pesanan):</span>
                <input
                  type="file"
                  accept="image/*"
                  data-testid={`admin-order-proof-input-${o.id}`}
                  onChange={(e) => uploadProof(o.id, e.target.files?.[0])}
                  disabled={uploading === o.id}
                  className="text-xs"
                />
                {uploading === o.id && <span className="text-xs text-stone-500">Mengupload…</span>}
                {proofs[o.id] && <span data-testid={`admin-order-proof-ok-${o.id}`} className="text-xs text-emerald-700 font-bold">✓ Terupload — pilih "Pesanan Selesai"</span>}
              </div>
            )}
            {(o.handover_photo_url || proofs[o.id]) && (
              <a
                data-testid={`admin-order-proof-${o.id}`}
                href={`${API}/files/${proofs[o.id] || o.handover_photo_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-block text-xs text-emerald-700 font-semibold underline"
              >
                📷 Lihat bukti foto serah terima
              </a>
            )}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Item</div>
                <ul className="mt-1 space-y-0.5">
                  {o.items.map((it, idx) => (
                    <li key={it.product_id + idx}>
                      {it.qty}× {it.name}{it.variant_name ? ` (${it.variant_name})` : ""} — {rupiah(it.qty * it.price)}
                      {it.design_file_path && (
                        <a
                          data-testid={`admin-design-link-${o.id}-${idx}`}
                          href={`${API}/files/${it.design_file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-emerald-700 font-semibold underline"
                        >
                          Lihat Desain ({it.design_file_name || "file"})
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-stone-500">Total</div>
                <div className="font-display font-black text-2xl text-emerald-800">{rupiah(o.total)}</div>
                <div className="text-xs text-stone-500">Bayar via {(o.payment_method || "cod").toUpperCase()}</div>
                {o.branch_id && <div className="text-xs text-stone-500">Cabang: <b>{branchName(o.branch_id)}</b>{o.distance_km > 0 ? ` · jarak ${o.distance_km} km` : ""}</div>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProductsTab({ focusSearch = "", onFocusDone }) {
  const { user } = useAuth();
  const inputOnly = !!user?.input_only;
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const myBranch = user?.branch_id || "";
  const [bulk, setBulk] = useState(null);
  const [bulkReport, setBulkReport] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showBulkWm, setShowBulkWm] = useState(false);
  const branchCtx = useBranch();
  const importBranch = myBranch ? branches.find((b) => b.id === myBranch) : (branchCtx?.branch || branches[0]);
  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  const stockLabel = (p) => {
    if (!FASMART_CATS.includes(p.category)) return `stok ${p.stock}`;
    if (myBranch) return `stok cabang ini: ${p.stock_by_branch?.[myBranch] ?? 0}`;
    return branches.map((b) => `${b.name.replace("FASMART ", "")}: ${p.stock_by_branch?.[b.id] ?? 0}`).join(" · ") || `stok ${p.stock}`;
  };

  const bulkUpload = async (e) => {
    const files = [...(e.target.files || [])].filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setBulk({ done: 0, total: files.length });
    let matched = 0;
    const unmatched = [];
    for (let i = 0; i < files.length; i += 8) {
      const fd = new FormData();
      files.slice(i, i + 8).forEach((f) => fd.append("files", f));
      try {
        const r = await api.post("/products/bulk-images", fd);
        matched += r.data.matched;
        unmatched.push(...r.data.unmatched);
      } catch {
        files.slice(i, i + 8).forEach((f) => unmatched.push({ file: f.name, reason: "request gagal" }));
      }
      setBulk({ done: Math.min(i + 8, files.length), total: files.length });
    }
    setBulk(null);
    setBulkReport({ matched, unmatched });
    load();
    e.target.value = "";
  };

  const onNew = () => { setEditing(null); setOpen(true); };
  const onEdit = (p) => { setEditing(p); setOpen(true); };
  const [prodSearch, setProdSearch] = useState("");
  useEffect(() => { if (focusSearch) { setProdSearch(focusSearch); onFocusDone?.(); } }, [focusSearch]);
  const filteredProducts = products.filter((p) => p.name.toLowerCase().includes(prodSearch.toLowerCase()) || (p.barcode || "").includes(prodSearch.trim()));
  const onDelete = async (p) => {
    const pin = await requestPin();
    if (!pin) return;
    try {
      await api.delete(`/products/${p.id}?pin=${pin}`);
      toast.success("Produk dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-bold text-xl">Katalog Produk ({prodSearch ? `${filteredProducts.length} dari ${products.length}` : products.length})</h3>
        <div className="flex items-center gap-2">
          <input id="bulk-photo-input" data-testid="admin-bulk-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={bulkUpload} />
          <label htmlFor="bulk-photo-input" data-testid="admin-bulk-photo-btn" className="cursor-pointer inline-flex items-center px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-emerald-700 transition">📷 Upload Foto Massal</label>
          {!myBranch && !inputOnly && <Button data-testid="admin-import-excel-btn" onClick={() => setShowImport(true)} variant="outline" className="rounded-full border-emerald-700 text-emerald-800 font-bold">📥 Import Excel</Button>}
          {!myBranch && !inputOnly && <Button data-testid="admin-bulk-watermark-btn" onClick={() => setShowBulkWm(true)} variant="outline" className="rounded-full border-amber-600 text-amber-700 font-bold">✨ Hapus Watermark Massal</Button>}
          <Button data-testid="admin-add-product-btn" onClick={onNew} className="rounded-full bg-emerald-700 hover:bg-emerald-800"><Plus className="w-4 h-4 mr-1" /> Tambah Produk</Button>
        </div>
      </div>
      <input data-testid="admin-product-search" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} placeholder="🔎 Cari produk (nama / barcode) untuk edit stok & harga…" className="mt-3 w-full text-sm border border-[#E5DECF] rounded-full px-4 py-2.5 bg-white" />
      {bulk && <div data-testid="admin-bulk-progress" className="mt-2 text-sm font-semibold text-amber-700">Mengupload & mencocokkan foto... {bulk.done}/{bulk.total}</div>}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredProducts.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#E5DECF] p-3 flex gap-3">
            {p.image_url ? <img src={p.image_url} alt="" className="w-20 h-20 rounded-xl object-cover" /> : <div className="w-20 h-20 rounded-xl bg-stone-100 flex items-center justify-center text-2xl shrink-0">🛒</div>}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">{p.category}</div>
              <div className="font-bold text-sm leading-tight line-clamp-2">{p.name}{p.price_locked ? <span data-testid={`admin-product-locked-${p.id}`} title="Dikunci super admin — harga tidak bisa diubah staff/kasir" className="ml-1">🔒</span> : null}</div>
              <div className="text-emerald-800 font-display font-bold text-sm mt-1">{rupiah(branchPrice(p, myBranch))}<span className="text-xs text-stone-500 font-normal">/{p.unit} · {stockLabel(p)}</span></div>
              <div className="mt-2 flex gap-2">
                <button data-testid={`admin-edit-product-${p.id}`} onClick={() => onEdit(p)} className="text-xs px-2 py-1 rounded-full bg-stone-100 hover:bg-stone-200"><Pencil className="w-3 h-3 inline mr-1" />Edit</button>
                {!inputOnly && <button data-testid={`admin-delete-product-${p.id}`} onClick={() => onDelete(p)} className="text-xs px-2 py-1 rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200"><Trash2 className="w-3 h-3 inline mr-1" />Hapus</button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {bulkReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setBulkReport(null)}>
          <div data-testid="admin-bulk-report" className="bg-white rounded-2xl max-w-md w-full p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black text-lg">Hasil Upload Foto</h3>
            <p className="text-sm mt-1 text-emerald-800 font-bold">✓ {bulkReport.matched} foto berhasil dipasang ke produk</p>
            {bulkReport.unmatched.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-bold text-rose-700">✗ {bulkReport.unmatched.length} file tidak cocok:</p>
                <ul className="mt-1 space-y-1 text-xs text-stone-600">
                  {bulkReport.unmatched.map((u, i) => <li key={i}>• {u.file} <span className="text-stone-400">({u.reason})</span></li>)}
                </ul>
                <p className="text-xs text-stone-500 mt-2">Tips: samakan nama file dengan nama produk persis, lalu upload ulang file yang gagal saja.</p>
              </div>
            )}
            <Button data-testid="admin-bulk-report-close" onClick={() => setBulkReport(null)} className="mt-4 w-full rounded-full bg-emerald-700 hover:bg-emerald-800">Tutup</Button>
          </div>
        </div>
      )}
      {showImport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowImport(false)}>
          <div data-testid="admin-import-excel-modal" className="bg-[#F6F1E7] rounded-2xl max-w-lg w-full p-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <ImportExcel branchId={importBranch?.id || ""} branchName={(importBranch?.name || "").replace("FASMART ", "")} onDone={load} />
            <Button data-testid="admin-import-excel-close" onClick={() => setShowImport(false)} className="mt-3 w-full rounded-full bg-emerald-700 hover:bg-emerald-800">Tutup</Button>
          </div>
        </div>
      )}
      {showBulkWm && <BulkWatermark onClose={() => setShowBulkWm(false)} onDone={load} />}
      <ProductDialog open={open} setOpen={setOpen} editing={editing} onSaved={load} branches={branches} myBranch={myBranch} inputOnly={inputOnly} />
    </div>
  );
}

function ProductDialog({ open, setOpen, editing, onSaved, branches = [], myBranch = "", inputOnly = false }) {
  const [form, setForm] = useState({
    name: "", category: "Sembako", price: 0, unit: "pcs", stock: 100,
    image_url: "", description: "", is_promo: false, promo_percent: 0,
    variants: [], lead_time: "", needs_design: false,
  });

  useEffect(() => {
    if (editing) setForm({
      variants: [], lead_time: "", needs_design: false, ...editing,
      stock: myBranch ? (editing.stock_by_branch?.[myBranch] ?? 0) : editing.stock,
      price: myBranch ? (editing.price_by_branch?.[myBranch] ?? editing.price) : editing.price,
    });
    else setForm({ name: "", category: "Sembako", price: 0, unit: "pcs", stock: 100, image_url: "", description: "", is_promo: false, promo_percent: 0, variants: [], lead_time: "", needs_design: false, stock_by_branch: {}, price_by_branch: {}, barcode: "", harga_modal: 0, grosir_price: 0, grosir_min: 0 });
  }, [editing, open]);

  const [aiBusy, setAiBusy] = useState(false);
  const aiScan = async (file) => {
    if (!file) return;
    setAiBusy(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
      const r = await api.post("/admin/products/ai-scan", { image_base64: b64 });
      const d = r.data || {};
      setForm((f) => ({
        ...f,
        ...(d.name ? { name: d.name } : {}),
        ...(d.category ? { category: d.category } : {}),
        ...(d.barcode ? { barcode: String(d.barcode) } : {}),
        ...(d.unit ? { unit: d.unit } : {}),
        ...(Number(d.price) > 0 ? { price: Number(d.price) } : {}),
        ...(Number(d.harga_modal) > 0 ? { harga_modal: Number(d.harga_modal) } : {}),
      }));
      toast.success("AI membaca foto — periksa & sesuaikan sebelum Simpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI gagal membaca foto — coba lebih dekat & terang"); }
    finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Nama dan harga wajib diisi");
    try {
      let payload = form;
      if (editing) {
        const diffDict = (now, before) => Object.fromEntries(Object.entries(now || {}).filter(([k, v]) => (before?.[k] ?? 0) !== v));
        payload = { ...form, stock_by_branch: diffDict(form.stock_by_branch, editing.stock_by_branch), price_by_branch: diffDict(form.price_by_branch, editing.price_by_branch) };
      }
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Produk tersimpan");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  const [uploadingImg, setUploadingImg] = useState(false);
  const [wmBusy, setWmBusy] = useState(false);
  const removeWatermark = async () => {
    if (!form.image_url || wmBusy) return;
    setWmBusy(true);
    try {
      const r = await api.post("/products/remove-watermark", { image_url: form.image_url });
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success("Watermark dihapus — gambar bersih terpasang");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus watermark"); }
    finally { setWmBusy(false); }
  };
  const [cost, setCost] = useState({ beli: "", diskon: "", jumlah: "" });
  const modalBeli = Math.max(0, (parseInt(cost.beli || "0", 10) || 0) - (parseInt(cost.diskon || "0", 10) || 0));
  const parseNum = (s) => (s === "" || s == null ? 0 : Math.max(0, parseInt(s, 10) || 0));
  const applyModal = (modal) => {
    const auto = suggestPrice(form.category, modal);
    setForm((f) => ({ ...f, harga_modal: modal, ...(auto > 0 && !f.price ? { price: auto } : {}) }));
  };
  const suggestedPrice = suggestPrice(form.category, form.harga_modal);
  const updCost = (k, v) => {
    const next = { ...cost, [k]: v };
    setCost(next);
    const mb = Math.max(0, (parseInt(next.beli || "0", 10) || 0) - (parseInt(next.diskon || "0", 10) || 0));
    const j = parseInt(next.jumlah || "0", 10) || 0;
    if (mb > 0 && j > 0) applyModal(Math.round(mb / j));
  };
  const [showScanner, setShowScanner] = useState(false);
  const pasteImage = async (e) => {
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
    if (!item) return;
    e.preventDefault();
    const file = item.getAsFile();
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file, `pasted.${file.type.split("/")[1] || "png"}`);
      const r = await api.post("/uploads/product-image", fd);
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success("Gambar produk terupload");
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal upload gambar"); }
    finally { setUploadingImg(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{myBranch ? (editing ? "Stok & Harga Cabang" : "Tambah Produk Cabang") : editing ? "Edit Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!myBranch && (
            <div>
              <input id="ai-scan-file" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) aiScan(f); }} />
              <label htmlFor="ai-scan-file" data-testid="ai-scan-btn" className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-2xl border-2 border-dashed text-sm font-bold cursor-pointer transition ${aiBusy ? "border-amber-300 bg-amber-50 text-amber-700" : "border-emerald-300 bg-emerald-50/60 text-emerald-800 hover:bg-emerald-50"}`}>
                📷 {aiBusy ? "AI sedang membaca foto…" : "AI Kamera — foto barang, form terisi otomatis"}
              </label>
            </div>
          )}
          <div>
            <Label>Nama Produk</Label>
            <Input data-testid="admin-product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" disabled={!!myBranch || inputOnly} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })} disabled={!!myBranch || inputOnly}>
                <SelectTrigger data-testid="admin-product-category" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{(myBranch ? FASMART_CATS : CATS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Satuan</Label>
              <Input data-testid="admin-product-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1" disabled={!!myBranch} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{myBranch ? "Harga Cabang (Rp)" : "Harga Jual (Rp)"}</Label>
              <Input data-testid="admin-product-price" type="number" value={form.price || ""} onChange={(e) => setForm({ ...form, price: parseNum(e.target.value) })} className="mt-1" placeholder="0" />
            </div>
            <div>
              <Label>{myBranch ? "Stok Cabang Ini" : "Stok"}</Label>
              <Input data-testid="admin-product-stock" type="number" value={form.stock || ""} onChange={(e) => setForm({ ...form, stock: parseNum(e.target.value) })} className="mt-1" placeholder="0" />
            </div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
            <div className="text-xs font-bold text-emerald-800 mb-2">🧮 Hitung Harga Modal</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px]">Harga Beli (Rp)</Label>
                <Input data-testid="admin-product-harga-beli" type="number" value={cost.beli} onChange={(e) => updCost("beli", e.target.value)} className="mt-1 bg-white" placeholder="total nota" />
              </div>
              <div>
                <Label className="text-[11px]">Diskon (Rp)</Label>
                <Input data-testid="admin-product-diskon-beli" type="number" value={cost.diskon} onChange={(e) => updCost("diskon", e.target.value)} className="mt-1 bg-white" placeholder="0" />
              </div>
              <div>
                <Label className="text-[11px]">Jumlah Beli (pcs)</Label>
                <Input data-testid="admin-product-jumlah-beli" type="number" value={cost.jumlah} onChange={(e) => updCost("jumlah", e.target.value)} className="mt-1 bg-white" placeholder="0" />
              </div>
            </div>
            <div className="mt-2 text-xs font-bold text-emerald-800" data-testid="admin-product-modal-beli">
              Modal Beli: {rupiah(modalBeli)}{modalBeli > 0 && Number(cost.jumlah) > 0 ? ` ÷ ${cost.jumlah} pcs → Harga Modal otomatis terisi` : ""}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Harga Modal (Rp)</Label>
              <Input data-testid="admin-product-harga-modal" type="number" value={form.harga_modal || ""} onChange={(e) => applyModal(parseNum(e.target.value))} className="mt-1" placeholder="0" disabled={!!myBranch} />
              {suggestedPrice > 0 && Number(form.price) > 0 && Number(form.price) !== suggestedPrice && (
                <div data-testid="admin-product-price-suggestion" className="text-[10px] text-amber-600 font-semibold mt-1">Saran margin {CATEGORY_MARGIN[form.category] ?? 15}% ({form.category}): {rupiah(suggestedPrice)}</div>
              )}
            </div>
            <div>
              <Label>Harga Grosir (Rp)</Label>
              <Input data-testid="admin-product-grosir-price" type="number" value={form.grosir_price || ""} onChange={(e) => setForm({ ...form, grosir_price: parseNum(e.target.value) })} className="mt-1" placeholder="0" disabled={!!myBranch} />
            </div>
            <div>
              <Label>Min. Grosir (pcs)</Label>
              <Input data-testid="admin-product-grosir-min" type="number" value={form.grosir_min || ""} onChange={(e) => setForm({ ...form, grosir_min: parseNum(e.target.value) })} className="mt-1" placeholder="0" disabled={!!myBranch} />
            </div>
          </div>
          <p className="text-[11px] text-stone-400 -mt-1">Harga grosir otomatis dipakai di kasir bila jumlah pembelian mencapai minimum. Isi 0 untuk menonaktifkan. Harga jual otomatis mengikuti margin per kategori (Sembako 6%, Minuman 18%, dst — bulat Rp100 terdekat) hanya bila kolom harga jual masih kosong — harga yang sudah terisi tidak akan ditimpa otomatis.</p>
          {Number(form.price) > 0 && Number(form.harga_modal) > 0 && (
            <div data-testid="admin-product-profit-info" className={`text-xs font-bold rounded-xl px-3 py-2 ${Number(form.price) - Number(form.harga_modal) >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
              💰 Profit eceran: {Number(form.price) - Number(form.harga_modal) >= 0 ? "+" : "−"}{rupiah(Math.abs(Number(form.price) - Number(form.harga_modal)))} ({(Math.abs((Number(form.price) - Number(form.harga_modal)) / Number(form.harga_modal)) * 100).toFixed(1).replace(".", ",")}% dari modal)
              {Number(form.grosir_price) > 0 && <span className="text-amber-700"> · Profit grosir: {Number(form.grosir_price) - Number(form.harga_modal) >= 0 ? "+" : "−"}{rupiah(Math.abs(Number(form.grosir_price) - Number(form.harga_modal)))} ({(Math.abs((Number(form.grosir_price) - Number(form.harga_modal)) / Number(form.harga_modal)) * 100).toFixed(1).replace(".", ",")}%)</span>}
            </div>
          )}
          <div>
            <Label>URL Gambar</Label>
            <Input data-testid="admin-product-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} onPaste={pasteImage} className="mt-1" placeholder="https://... atau Ctrl+V paste gambar di kolom ini" disabled={!!myBranch} />
            {uploadingImg && <div data-testid="admin-image-uploading" className="text-xs text-amber-600 mt-1 font-semibold">Mengupload gambar...</div>}
            {form.image_url && (
              <div className="mt-2 flex items-start gap-3">
                <img src={form.image_url} alt="preview" className="h-24 rounded-lg object-cover" />
                {!myBranch && !inputOnly && (
                  <button type="button" data-testid="admin-remove-watermark-btn" onClick={removeWatermark} disabled={wmBusy} className="px-3 py-2 rounded-full bg-stone-900 text-white text-xs font-bold hover:bg-emerald-700 transition disabled:opacity-50">
                    {wmBusy ? "⏳ Menghapus watermark…" : "✨ Hapus Watermark"}
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <Label>Barcode (opsional, untuk scan di kasir)</Label>
            <div className="flex gap-1.5 mt-1">
              <Input data-testid="admin-product-barcode" value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="cth: 8991234567890" disabled={!!myBranch} />
              {!myBranch && (
                <button type="button" data-testid="admin-barcode-scan-btn" onClick={() => setShowScanner(true)} title="Scan barcode via kamera HP" className="shrink-0 px-3 rounded-lg bg-stone-900 text-white text-sm font-bold hover:bg-emerald-700 transition">📷 Scan</button>
              )}
            </div>
          </div>
          {showScanner && (
            <BarcodeScanner
              onResult={(code) => { setForm((f) => ({ ...f, barcode: code })); setShowScanner(false); toast.success(`Barcode terscan: ${code}`); }}
              onClose={() => setShowScanner(false)}
            />
          )}
          <div className="flex items-center justify-between">
            <Label>Aktifkan Promo</Label>
            <Switch data-testid="admin-product-promo" checked={form.is_promo} onCheckedChange={(v) => setForm({ ...form, is_promo: v })} disabled={!!myBranch} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Lead Time (mis. "± 1 hari")</Label>
              <Input data-testid="admin-product-lead-time" value={form.lead_time} onChange={(e) => setForm({ ...form, lead_time: e.target.value })} className="mt-1" placeholder="± 2 jam" disabled={!!myBranch} />
            </div>
            <div className="flex items-center justify-between pt-6">
              <Label>Butuh Desain</Label>
              <Switch data-testid="admin-product-needs-design" checked={form.needs_design} onCheckedChange={(v) => setForm({ ...form, needs_design: v })} disabled={!!myBranch} />
            </div>
          </div>
          {form.is_promo && (
            <div>
              <Label>Diskon (%)</Label>
              <Input data-testid="admin-product-promo-percent" type="number" value={form.promo_percent || ""} onChange={(e) => setForm({ ...form, promo_percent: parseNum(e.target.value) })} className="mt-1" placeholder="0" />
            </div>
          )}
          {!myBranch && editing && FASMART_CATS.includes(form.category) && branches.length > 0 && (
            <div>
              <Label>Stok per Cabang</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {branches.map((b) => (
                  <div key={b.id}>
                    <div className="text-[10px] font-semibold text-stone-500 mb-0.5 truncate">{b.name.replace("FASMART ", "")}</div>
                    <Input data-testid={`admin-product-branch-stock-${b.id}`} type="number" value={form.stock_by_branch?.[b.id] || ""} onChange={(e) => setForm({ ...form, stock_by_branch: { ...(form.stock_by_branch || {}), [b.id]: parseNum(e.target.value) } })} placeholder="0" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <Button data-testid="admin-save-product-btn" onClick={save} className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 font-bold">Simpan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FasnetTab() {
  const [regs, setRegs] = useState([]);
  const load = () => api.get("/fasnet/registrations").then((r) => setRegs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const FSTATUS = {
    baru: { label: "Pendaftar Baru", color: "bg-amber-100 text-amber-800" },
    survey: { label: "Survey Lokasi", color: "bg-sky-100 text-sky-800" },
    terpasang: { label: "Terpasang", color: "bg-emerald-100 text-emerald-800" },
    batal: { label: "Batal", color: "bg-rose-100 text-rose-800" },
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/fasnet/registrations/${id}/status`, { status });
      toast.success("Status pendaftaran diperbarui");
      load();
    } catch { toast.error("Gagal update status"); }
  };

  return (
    <div className="mt-6 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-xl">Pendaftaran & Mitra FASNET ({regs.length})</h3>
      </div>
      {regs.length === 0 && <p className="text-sm text-stone-500">Belum ada pendaftaran masuk.</p>}
      {regs.map((r) => (
        <div key={r.id} data-testid={`admin-fasnet-reg-${r.id}`} className="bg-white rounded-3xl border border-[#E5DECF] p-5">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">#{r.id.slice(-6).toUpperCase()} · {new Date(r.created_at).toLocaleString("id-ID")}</div>
              <div className="font-display font-bold text-lg">{r.name} · {r.phone}</div>
              {r.apply_type === "mitra" && (
                <span data-testid={`admin-fasnet-mitra-badge-${r.id}`} className="tag-pill bg-purple-100 text-purple-800 mt-1 w-fit">🤝 Pengajuan Mitra</span>
              )}
              <div className="text-xs text-stone-600 max-w-lg">{r.address}</div>
              {r.notes && <div className="text-xs text-stone-500 mt-0.5 max-w-lg">Catatan: {r.notes}</div>}
              {r.referral_name && (
                <div data-testid={`admin-fasnet-referral-${r.id}`} className="text-xs text-orange-600 font-semibold mt-1">
                  🎁 Diajak oleh: {r.referral_name} — hadiah: ½ kg gula pasir + 5 Indomie kaldu
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={`tag-pill ${FSTATUS[r.status]?.color || ""}`}>{FSTATUS[r.status]?.label || r.status}</span>
              <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                <SelectTrigger data-testid={`admin-fasnet-status-${r.id}`} className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FSTATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function KostTab() {
  const [bookings, setBookings] = useState([]);
  const load = () => api.get("/kost/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const KSTATUS = {
    baru: { label: "Booking Baru", color: "bg-amber-100 text-amber-800" },
    dijadwalkan: { label: "Kunjungan Dijadwalkan", color: "bg-sky-100 text-sky-800" },
    selesai: { label: "Deal / Masuk", color: "bg-emerald-100 text-emerald-800" },
    batal: { label: "Batal", color: "bg-rose-100 text-rose-800" },
  };
  const ROOMS = {
    kipas: "Kipas + Toilet Luar (650rb/bln)",
    ac: "AC + Toilet Dalam (1jt/bln)",
  };
  const KBRANCH = {
    "kost-01": "FAS KOST 01 (Blok A No. 12)",
    "kost-02": "FAS KOST 02 (Blok B No. 9)",
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/kost/bookings/${id}/status`, { status });
      toast.success("Status booking diperbarui");
      load();
    } catch { toast.error("Gagal update status"); }
  };

  return (
    <div className="mt-6 space-y-3">
      <h3 className="font-display font-bold text-xl">Booking Kunjungan FAS KOST ({bookings.length})</h3>
      {bookings.length === 0 && <p className="text-sm text-stone-500">Belum ada booking kunjungan masuk.</p>}
      {bookings.map((b) => (
        <div key={b.id} data-testid={`admin-kost-booking-${b.id}`} className="bg-white rounded-3xl border border-[#E5DECF] p-5">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">#{b.id.slice(-6).toUpperCase()} · {new Date(b.created_at).toLocaleString("id-ID")}</div>
              <div className="font-display font-bold text-lg">{b.name} · {b.phone}</div>
              <div className="text-xs text-stone-600">Cabang: <b>{KBRANCH[b.branch] || KBRANCH["kost-01"]}</b> · Kamar: <b>{ROOMS[b.room_type] || b.room_type}</b>{b.visit_date ? ` · Kunjungan: ${b.visit_date}` : ""}</div>
              {b.notes && <div className="text-xs text-stone-500 mt-1">Catatan: {b.notes}</div>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`tag-pill ${KSTATUS[b.status]?.color || ""}`}>{KSTATUS[b.status]?.label || b.status}</span>
              <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                <SelectTrigger data-testid={`admin-kost-status-${b.id}`} className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(KSTATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RakitTab() {
  const [bookings, setBookings] = useState([]);
  const load = () => api.get("/rumahrakit/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const RSTATUS = {
    baru: { label: "Booking Baru", color: "bg-amber-100 text-amber-800" },
    terkonfirmasi: { label: "Terkonfirmasi", color: "bg-sky-100 text-sky-800" },
    selesai: { label: "Selesai", color: "bg-emerald-100 text-emerald-800" },
    batal: { label: "Batal", color: "bg-rose-100 text-rose-800" },
  };
  const PKG = {
    rakit_12: "Rumah Rakit 12 Jam (450rb)",
    rakit_24: "Rumah Rakit 24 Jam Nginap (900rb)",
    speedboat_12: "SpeedBoat 15PK 12 Jam (350rb)",
    speedboat_24: "SpeedBoat 15PK 24 Jam (500rb)",
    bbm: "BBM SpeedBoat/Sampan (15rb/liter)",
    sampan_12: "Sampan/Kato 12 Jam (100rb)",
    sampan_24: "Sampan/Kato 24 Jam (150rb)",
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/rumahrakit/bookings/${id}/status`, { status });
      toast.success("Status booking diperbarui");
      load();
    } catch { toast.error("Gagal update status"); }
  };

  return (
    <div className="mt-6 space-y-3">
      <h3 className="font-display font-bold text-xl">Booking RUMAH RAKIT ({bookings.length})</h3>
      {bookings.length === 0 && <p className="text-sm text-stone-500">Belum ada booking masuk.</p>}
      {bookings.map((b) => (
        <div key={b.id} data-testid={`admin-rakit-booking-${b.id}`} className="bg-white rounded-3xl border border-[#E5DECF] p-5">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">#{b.id.slice(-6).toUpperCase()} · {new Date(b.created_at).toLocaleString("id-ID")}</div>
              <div className="font-display font-bold text-lg">{b.name} · {b.phone}</div>
              <div className="text-xs text-stone-600">Paket: <b>{PKG[b.package] || b.package}</b>{b.visit_date ? ` · Tanggal: ${b.visit_date}` : ""}</div>
              {b.notes && <div className="text-xs text-stone-500 mt-1">Catatan: {b.notes}</div>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`tag-pill ${RSTATUS[b.status]?.color || ""}`}>{RSTATUS[b.status]?.label || b.status}</span>
              <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                <SelectTrigger data-testid={`admin-rakit-status-${b.id}`} className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(RSTATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MusicTab() {
  const [regs, setRegs] = useState([]);
  const load = () => api.get("/music/registrations").then((r) => setRegs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const MSTATUS = {
    baru: { label: "Pendaftaran Baru", color: "bg-amber-100 text-amber-800" },
    dijadwalkan: { label: "Jadwal Terkonfirmasi", color: "bg-sky-100 text-sky-800" },
    aktif: { label: "Murid Aktif", color: "bg-emerald-100 text-emerald-800" },
    batal: { label: "Batal", color: "bg-rose-100 text-rose-800" },
  };
  const PKG = { drum: "Les Drum", piano: "Les Piano", gitar: "Les Gitar/Bass", vokal: "Les Vokal" };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/music/registrations/${id}/status`, { status });
      toast.success("Status pendaftaran diperbarui");
      load();
    } catch { toast.error("Gagal update status"); }
  };

  return (
    <div className="mt-6 space-y-3">
      <h3 className="font-display font-bold text-xl">Pendaftaran Les FAS MUSIC ({regs.length})</h3>
      {regs.length === 0 && <p className="text-sm text-stone-500">Belum ada pendaftaran les masuk.</p>}
      {regs.map((r) => (
        <div key={r.id} data-testid={`admin-music-reg-${r.id}`} className="bg-white rounded-3xl border border-[#E5DECF] p-5">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">#{r.id.slice(-6).toUpperCase()} · {new Date(r.created_at).toLocaleString("id-ID")}</div>
              <div className="font-display font-bold text-lg">{r.student_name} · {r.phone}</div>
              <div className="text-xs text-stone-600">
                Paket: <b>{PKG[r.package] || r.package}</b> · Jadwal: <b>{r.day} {r.time_slot}</b> · Instruktur: <b>{r.instructor}</b>
              </div>
              {r.notes && <div className="text-xs text-stone-500 mt-1">Catatan: {r.notes}</div>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`tag-pill ${MSTATUS[r.status]?.color || ""}`}>{MSTATUS[r.status]?.label || r.status}</span>
              <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                <SelectTrigger data-testid={`admin-music-status-${r.id}`} className="w-52"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MSTATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  return (
    <div className="mt-6 bg-white rounded-3xl border border-[#E5DECF] p-4">
      <h3 className="font-display font-bold text-xl mb-3">Pelanggan Warung ({customers.length})</h3>
      {customers.length === 0 && <p className="text-sm text-stone-500">Belum ada pelanggan.</p>}
      <div className="space-y-2">
        {customers.map((c) => (
          <div key={c.user_id} data-testid={`admin-customer-${c.user_id}`} className="flex items-center gap-3 p-3 rounded-xl border border-[#E5DECF]">
            <div className="w-10 h-10 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center">{c.name?.[0] || "U"}</div>
            <div className="flex-1">
              <div className="font-bold">{c.name}</div>
              <div className="text-xs text-stone-500">{c.email}</div>
            </div>
            <div className="text-right">
              <div className="tag-pill bg-amber-100 text-amber-800">{c.tier}</div>
              <div className="text-xs text-stone-600 mt-1">{c.poin_berkah} poin · {rupiah(c.total_spent || 0)}</div>
            </div>
            <button data-testid={`admin-delete-customer-${c.user_id}`} title="Hapus pelanggan (khusus super admin)" onClick={async () => { const pin = await requestPin(); if (!pin) return; try { await api.delete(`/admin/customers/${c.user_id}?pin=${pin}`); toast.success("Pelanggan dihapus"); setCustomers((cs) => cs.filter((x) => x.user_id !== c.user_id)); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus (khusus super admin)"); } }} className="w-8 h-8 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center text-sm">🗑</button>
          </div>
        ))}
      </div>
    </div>
  );
}
